import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { service, factories, models } from 'powerbi-client';

const PowerBIReports = () => {
  const [reportInfo, setReportInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const reportRef = useRef(null);
  const powerbiRef = useRef(null);
  const { user } = useAuth();

  const addLog = useCallback((message) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const getApiUrl = useCallback((endpoint) => {
    const API_BASE_URL = process.env.NODE_ENV === 'production' 
      ? 'https://changemanagement-backend-ahfre3hvgcerbrgp.canadacentral-01.azurewebsites.net/api'
      : 'http://localhost:5000/api';
    return `${API_BASE_URL}${endpoint}`;
  }, []);

  // PowerBI embedding effect - using correct PowerBI SDK v2.23.1
  useEffect(() => {
    if (!reportInfo?.embed_url || !reportInfo?.embed_token || !reportInfo?.report_id) {
      return;
    }

    // 🔧 Copy ref to variable to avoid stale reference in cleanup
    const reportContainer = reportRef.current;
    const powerbiService = powerbiRef.current;

    try {
      addLog('🔧 Configuring PowerBI embed...');

      const embedConfig = {
        type: 'report',
        id: reportInfo.report_id,
        embedUrl: reportInfo.embed_url,
        accessToken: reportInfo.embed_token,
        tokenType: models.TokenType.Embed,
        settings: {
          panes: {
            filters: { visible: false },
            pageNavigation: { visible: true },
          },
          background: models.BackgroundType.Transparent,
        },
      };

      addLog('🧹 Resetting PowerBI container...');
      
      // ✅ Correct way to reset container with powerbi-client v2.23.1
      if (powerbiService) {
        console.log('🔍 Using existing PowerBI service:', powerbiService);
        console.log('🔍 Available methods:', Object.getOwnPropertyNames(powerbiService));
        powerbiService.reset(reportContainer);
      } else {
        // Initialize the service if not already done
        console.log('🔍 Creating new PowerBI service...');
        powerbiRef.current = new service.Service(
          factories.hpmFactory,
          factories.wpmpFactory,
          factories.routerFactory
        );
        console.log('🔍 PowerBI service created:', powerbiRef.current);
        console.log('🔍 Available methods:', Object.getOwnPropertyNames(powerbiRef.current));
        console.log('🔍 Reset method available?', typeof powerbiRef.current.reset);
        powerbiRef.current.reset(reportContainer);
      }
      
      addLog('📊 Embedding PowerBI report...');

      // ✅ Correct way to embed with powerbi-client v2.23.1
      const report = powerbiRef.current.embed(reportContainer, embedConfig);
      
      addLog('✅ PowerBI report embed initiated');

      // Handle embed events
      report.on('loaded', () => {
        addLog('✅ PowerBI report loaded successfully!');
      });

      report.on('error', (event) => {
        const errorDetail = event.detail;
        addLog(`❌ PowerBI embed error: ${JSON.stringify(errorDetail)}`);
        console.error('PowerBI embed error:', errorDetail);
      });

      report.on('rendered', () => {
        addLog('🎨 PowerBI report rendered');
      });

      // ✅ Fixed cleanup function - using copied variables instead of refs
      return () => {
        if (powerbiRef.current && reportContainer) {
          try {
            powerbiRef.current.reset(reportContainer);
            addLog('🧹 PowerBI embed cleaned up');
          } catch (e) {
            console.warn('Error during PowerBI cleanup:', e);
          }
        }
      };

    } catch (embedError) {
      addLog(`❌ Embed error: ${embedError.message}`);
      console.error('PowerBI embed error:', embedError);
    }
  }, [reportInfo?.embed_url, reportInfo?.embed_token, reportInfo?.report_id, addLog]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const loadReport = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Step 1: Initial connection
        addLog('🔗 STEP 1: Connecting to PowerBI service...');
        addLog('📧 STEP 2: Getting user authentication...');
        addLog(`👤 User: ${user.name} (${user.email})`);
        
        // Step 3: API Call
        addLog('🌐 STEP 3: Making API call to /powerbi/report');
        const apiUrl = getApiUrl('/powerbi/report');
        addLog(`📡 API URL: ${apiUrl}`);
        
        const headers = getAuthHeaders();
        addLog('🔐 STEP 4: Using JWT authentication');

        const response = await axios.get(apiUrl, {
          headers: headers,
          timeout: 30000
        });
        
        addLog('✅ STEP 5: PowerBI API response received');
        addLog(`📊 Response Status: ${response.status}`);
        
        if (response.data) {
          // Log detailed debug information
          addLog('🔍 STEP 6: Processing response data...');
          
          if (response.data.rls_user) {
            addLog(`👤 RLS User: ${response.data.rls_user}`);
          }
          if (response.data.token_method) {
            addLog(`🔧 Token Method: ${response.data.token_method}`);
          }
          if (response.data.workspace_id) {
            addLog(`🏢 Workspace ID: ${response.data.workspace_id}`);
          }
          if (response.data.report_id) {
            addLog(`📄 Report ID: ${response.data.report_id}`);
          }
          if (response.data.dataset_id) {
            addLog(`📊 Dataset ID: ${response.data.dataset_id}`);
          }
          
          setReportInfo(response.data);
          addLog(`✅ STEP 7: Report loaded: ${response.data.report_name}`);
          addLog(`📱 Embed URL: ${response.data.embed_url.substring(0, 50)}...`);
          if (response.data.embed_token) {
            addLog(`🔐 STEP 8: Embed token received: ${response.data.embed_token.substring(0, 20)}...`);
            addLog(`⏰ Token expires: ${response.data.expiration}`);
          } else {
            addLog(`❌ STEP 8: No embed token received!`);
          }
        } else {
          throw new Error('No report data received');
        }

      } catch (err) {
        console.error('Error loading report:', err);
        
        addLog('❌ ERROR OCCURRED - Analyzing response...');
        
        // Enhanced error logging with detailed debug information
        const errorData = err.response?.data;
        const errorStatus = err.response?.status;
        const errorStatusText = err.response?.statusText;
        
        addLog(`🔴 HTTP Status: ${errorStatus} ${errorStatusText}`);
        
        if (errorData) {
          addLog(`❌ Error Message: ${errorData.error || err.message}`);
          
          // Log detailed debug information if available
          if (errorData.rls_user) {
            addLog(`👤 RLS User: ${errorData.rls_user}`);
          }
          if (errorData.workspace_id) {
            addLog(`🏢 Workspace ID: ${errorData.workspace_id}`);
          }
          if (errorData.report_id) {
            addLog(`📄 Report ID: ${errorData.report_id}`);
          }
          if (errorData.dataset_id) {
            addLog(`📊 Dataset ID: ${errorData.dataset_id}`);
          }
          if (errorData.embed_url) {
            addLog(`📱 Embed URL: ${errorData.embed_url.substring(0, 50)}...`);
          }
          
          // Log API-specific errors
          if (errorData.regular_api_error) {
            addLog(`🔴 Regular API Error (${errorData.regular_api_error.status_code}):`);
            addLog(`   ${errorData.regular_api_error.error}`);
          }
          if (errorData.batch_api_error) {
            addLog(`🔴 Batch API Error (${errorData.batch_api_error.status_code}):`);
            addLog(`   ${errorData.batch_api_error.error}`);
          }
          if (errorData.debug_info) {
            addLog(`🔍 Debug Info: ${errorData.debug_info}`);
          }
          if (errorData.report_found !== undefined) {
            addLog(`📄 Report Found: ${errorData.report_found ? 'YES' : 'NO'}`);
          }
          if (errorData.report_name) {
            addLog(`📝 Report Name: ${errorData.report_name}`);
          }
          if (errorData.details) {
            addLog(`📋 Details: ${errorData.details}`);
          }
          
          // Log environment variable status
          if (errorData.error && errorData.error.includes('POWERBI_CHANGE_SATURATION')) {
            addLog(`🔧 Environment Variable Issue Detected`);
            addLog(`   Check POWERBI_CHANGE_SATURATION is set in Azure`);
          }
          
        } else {
          addLog(`❌ Network/Connection Error: ${err.message}`);
          if (err.code) {
            addLog(`🔌 Error Code: ${err.code}`);
          }
        }
        
        setError(`Failed to load report: ${errorData?.error || err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [user, getApiUrl, getAuthHeaders, addLog]);

  const retryLoadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      addLog('Retrying Change Management report...');

      const response = await axios.get(getApiUrl('/powerbi/report'), {
        headers: getAuthHeaders(),
        timeout: 30000
      });
      
      if (response.data) {
        setReportInfo(response.data);
        addLog(`✅ Report loaded: ${response.data.report_name}`);
        addLog(`📱 Embed URL: ${response.data.embed_url.substring(0, 50)}...`);
        if (response.data.embed_token) {
          addLog(`🔐 Embed token received: ${response.data.embed_token.substring(0, 20)}...`);
        } else {
          addLog(`❌ No embed token received!`);
        }
      } else {
        throw new Error('No report data received');
      }

          } catch (err) {
        console.error('Error loading report:', err);
        
        // Enhanced error logging with detailed debug information
        const errorData = err.response?.data;
        if (errorData) {
          addLog(`❌ Error: ${errorData.error || err.message}`);
          
          // Log detailed debug information if available
          if (errorData.rls_user) {
            addLog(`👤 RLS User: ${errorData.rls_user}`);
          }
          if (errorData.workspace_id) {
            addLog(`🏢 Workspace: ${errorData.workspace_id}`);
          }
          if (errorData.report_id) {
            addLog(`📄 Report ID: ${errorData.report_id}`);
          }
          if (errorData.dataset_id) {
            addLog(`📊 Dataset: ${errorData.dataset_id}`);
          }
          if (errorData.embed_url) {
            addLog(`📱 Embed URL: ${errorData.embed_url.substring(0, 50)}...`);
          }
          
          // Log API-specific errors
          if (errorData.regular_api_error) {
            addLog(`🔴 Regular API Error (${errorData.regular_api_error.status_code}): ${errorData.regular_api_error.error}`);
          }
          if (errorData.batch_api_error) {
            addLog(`🔴 Batch API Error (${errorData.batch_api_error.status_code}): ${errorData.batch_api_error.error}`);
          }
          if (errorData.debug_info) {
            addLog(`🔍 Debug Info: ${errorData.debug_info}`);
          }
          if (errorData.report_found !== undefined) {
            addLog(`📄 Report Found: ${errorData.report_found ? 'YES' : 'NO'}`);
          }
          if (errorData.report_name) {
            addLog(`📝 Report Name: ${errorData.report_name}`);
          }
        } else {
          addLog(`❌ Error: ${err.message}`);
        }
        
        setError(`Failed to load report: ${errorData?.error || err.message}`);
      } finally {
        setLoading(false);
      }
  };

  if (!user) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h3>Change Management Report</h3>
        <p>Please log in to view the reports</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '20px' }}>
        <div>Loading Change Saturation report...</div>
        <div style={{ marginTop: '20px', fontSize: '12px', fontFamily: 'monospace' }}>
          {logs.map((log, index) => (
            <div key={index}>{log}</div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ color: 'red', marginBottom: '20px' }}>
          Error: {error}
        </div>
        <button onClick={retryLoadReport} style={{ marginBottom: '20px', padding: '8px 16px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}>
          Retry
        </button>
        <div style={{ marginTop: '20px', fontSize: '12px', fontFamily: 'monospace' }}>
          <strong>Debug Logs:</strong>
          {logs.map((log, index) => (
            <div key={index}>{log}</div>
          ))}
        </div>
      </div>
    );
  }

  if (!reportInfo) {
    return (
      <div style={{ padding: '20px' }}>
        <div>No report data available</div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: 'calc(100vh - 120px)' }}>
      {/* PowerBI Report Container - Using correct PowerBI SDK v2.23.1 */}
      <div 
        ref={reportRef}
        style={{ 
          width: '100%', 
          height: '100%',
          border: 'none',
          background: '#f8f9fa'
        }}
      />
    </div>
  );
};

export default PowerBIReports;
